import { Badge } from '@/components/ui/badge';
import { buildCsv } from '@/lib/csv';
import { downloadText } from '@/lib/download';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { FreeFormItem } from '../types/freeFormItem';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const FreeFormItemManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFreeFormItem, setEditingFreeFormItem] = useState<FreeFormItem | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch free form items with server-side filtering and pagination
  const {
    data: freeFormItemsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'freeFormItems',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/freeFormItem?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('FreeFormItem API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const freeFormItems = freeFormItemsResponse?.data || [];
  const pagination = freeFormItemsResponse?.pagination;

  // Create mutation
  const createFreeFormItemMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/freeFormItem', data);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['freeFormItems'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormItem'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Item created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create free form item';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateFreeFormItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/freeFormItem/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeFormItems'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormItem'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Item updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingFreeFormItem(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update free form item';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteFreeFormItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/freeFormItem/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeFormItems'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormItem'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Item deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete free form item',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<FreeFormItem>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, freeFormItem) => (
        <div className="font-medium">{freeFormItem.name}</div>
      ),
    },
    {
      key: 'hsCode',
      label: 'HS Code',
      sortable: true,
      render: (_, freeFormItem) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {freeFormItem.hsCode}
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      render: (_, freeFormItem) => (
        <div className="text-sm text-muted-foreground max-w-xs truncate">
          {freeFormItem.description || '-'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, freeFormItem) => (
        <Badge
          variant={
            freeFormItem.status === 'Active' ? 'default' : 'secondary'
          }
          className={
            freeFormItem.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {freeFormItem.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, freeFormItem) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(freeFormItem.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Free Form Item Information',
      description: 'Configure free form item details for customs and shipping.',
      fields: [
        {
          name: 'name',
          label: 'Item Name',
          type: 'text',
          required: true,
          placeholder: 'Enter item name (e.g., "Electronic Components", "Textile Fabrics")',
          description: 'A unique descriptive name for this item',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'hsCode',
          label: 'HS Code',
          type: 'text',
          required: true,
          placeholder: 'Enter HS Code (e.g., "8471.30", "6204.62")',
          description: 'Harmonized System code assigned by World Customs Organisation',
          validation: z
            .string()
            .min(2, 'HS Code must be at least 2 characters')
            .max(20, 'HS Code cannot exceed 20 characters'),
        },
        {
          name: 'description',
          label: 'Description',
          type: 'textarea',
          required: false,
          placeholder: 'Enter a short description of the item (optional)',
          description: 'Brief description of the item for reference',
          validation: z
            .string()
            .max(500, 'Description cannot exceed 500 characters')
            .optional(),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this free form item',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingFreeFormItem(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (freeFormItem: FreeFormItem) => {
    setEditingFreeFormItem(freeFormItem);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (freeFormItem: FreeFormItem) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${freeFormItem.name}"? This action cannot be undone.`
      )
    ) {
      deleteFreeFormItemMutation.mutate(freeFormItem._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data to uppercase HS Code
    const transformedData = {
      ...data,
      hsCode: data.hsCode.toUpperCase(),
    };

    if (editingFreeFormItem) {
      updateFreeFormItemMutation.mutate({
        id: editingFreeFormItem._id,
        data: transformedData,
      });
    } else {
      createFreeFormItemMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingFreeFormItem(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Free form items data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Add current filters to export
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/freeFormItem/export?${params.toString()}`
      );

      if (response.data.success) {
        const freeFormItems = Array.isArray(response.data.data) ? response.data.data : [];

        if (freeFormItems.length === 0) {
          toast({
            title: 'No records to export',
            description: 'There are no free form items for the current filters.',
            variant: 'default',
          });
          return;
        }

        // Transform data for export
        const headers = [
          'Name',
          'HS Code',
          'Description',
          'Status',
          'Created At',
          'Updated At',
        ];
        const rows = freeFormItems.map((item: FreeFormItem) => [
          item.name,
          item.hsCode,
          item.description || '',
          item.status,
          new Date(item.createdAt).toLocaleString(),
          new Date(item.updatedAt).toLocaleString(),
        ]);
        const csvContent = buildCsv(headers, rows, { includeBom: true, quoteStrings: true });

        downloadText(
          csvContent,
          `free-form-items-${new Date().toISOString().split('T')[0]}.csv`,
          'text/csv;charset=utf-8;'
        );

        toast({
          title: 'Success',
          description: `Exported ${freeFormItems.length} free form items`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export free form items',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Free Form Item Management
          </h1>
          <p className='text-muted-foreground'>
            Manage free form items with HS codes for customs and shipping
          </p>
        </div>
      </div>

      <MasterDataTable<FreeFormItem>
        title='Free Form Items'
        description='Manage and configure free form items with HS codes'
        data={freeFormItems}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search free form items by name, HS code, or description...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingFreeFormItem ? 'Edit Free Form Item' : 'Create Free Form Item'}
        sections={formSections}
        initialData={editingFreeFormItem}
        onSubmit={handleSubmit}
        isLoading={
          createFreeFormItemMutation.isPending || updateFreeFormItemMutation.isPending
        }
        mode={editingFreeFormItem ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default FreeFormItemManagement;
